import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { UserPlus } from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useToast } from '@/components/ui/use-toast';

interface InviteSamenMemberDialogProps {
  samenId: string;
  onInvited?: () => void;
}

/**
 * Invites one person to a samen group by name and email. Any member can send
 * one -- the same people who can already rename the group and change its photo.
 *
 * The edge function decides what the mail says: someone who already has a
 * HoofdnaarHart account gets a join link, someone who doesn't gets a signup
 * link carrying an invite code tied to this group.
 */
export function InviteSamenMemberDialog({
  samenId,
  onInvited,
}: InviteSamenMemberDialogProps) {
  const [open, setOpen] = useState(false);
  const [naam, setNaam] = useState('');
  const [email, setEmail] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { toast } = useToast();

  const reset = () => {
    setNaam('');
    setEmail('');
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!naam.trim() || !email.trim() || submitting) return;

    setSubmitting(true);
    try {
      const { data, error } = await supabase.functions.invoke('samen-invite', {
        body: {
          samenId,
          naam: naam.trim(),
          email: email.trim(),
          origin: window.location.origin,
        },
      });

      // A non-2xx from the function arrives as FunctionsHttpError with the
      // body still on the response, so dig the Dutch message out of it rather
      // than showing "Edge Function returned a non-2xx status code".
      if (error) {
        const detail = await readFunctionError(error);
        throw new Error(detail ?? error.message);
      }
      if (data?.error) throw new Error(data.error);

      toast({
        title: 'Uitnodiging verstuurd',
        description:
          data?.kind === 'signup'
            ? `${naam.trim()} heeft nog geen account en krijgt een mail om er een aan te maken.`
            : `${naam.trim()} krijgt een mail om lid te worden van de groep.`,
      });
      reset();
      setOpen(false);
      onInvited?.();
    } catch (err) {
      console.error('Failed to send samen invite:', err);
      toast({
        title: 'Versturen mislukt',
        description:
          err instanceof Error ? err.message : 'Probeer het later opnieuw.',
        variant: 'destructive',
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) reset();
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="sm" className="gap-2">
          <UserPlus className="h-4 w-4" />
          Iemand uitnodigen
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Iemand uitnodigen</DialogTitle>
          <DialogDescription>
            Vul de naam en het e-mailadres in. Diegene krijgt een mail waarmee
            hij of zij zichzelf aan de groep kan toevoegen.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="inviteNaam">Naam</Label>
            <Input
              id="inviteNaam"
              value={naam}
              onChange={(e) => setNaam(e.target.value)}
              placeholder="Voor- en achternaam"
              maxLength={80}
              autoFocus
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="inviteEmail">E-mailadres</Label>
            <Input
              id="inviteEmail"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="naam@voorbeeld.nl"
            />
          </div>
          <DialogFooter>
            <Button
              type="submit"
              disabled={!naam.trim() || !email.trim() || submitting}
            >
              {submitting ? 'Versturen...' : 'Uitnodiging versturen'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

async function readFunctionError(error: unknown): Promise<string | null> {
  const res = (error as { context?: Response })?.context;
  if (!res || typeof res.json !== 'function') return null;
  try {
    const body = await res.json();
    return typeof body?.error === 'string' ? body.error : null;
  } catch {
    return null;
  }
}
